import React, { useState, useEffect } from 'react';
import useTranslate from '../../Hooks/useTranslate';
import { Button, IconButton, Typography, Paper, Collapse } from '@mui/material';
import { CenteredFlexBox } from '../../Components/FlexBox';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import AddIcon from '@mui/icons-material/Add';
import { getFullPath, useFieldAtom } from './useFieldValue';
import { SchemaRenderer } from './SchemaRenderer';
import CollapseToggle from '../Button/CollapseToggle';

export const ArrayField = ({ basePath, field }) => {
  const translate = useTranslate();
  const path = getFullPath(basePath, field.key);

  const [items, setItems] = useFieldAtom(path, field.defaultValue || []);
  const [collapsed, setCollapsed] = useState(false);

  // Seeds the store with the declared items without marking the form as changed.
  useEffect(() => {
    const expected = JSON.stringify(field.defaultValue || []);
    const current = JSON.stringify(items || []);

    if (expected !== current) {
      setItems(field.defaultValue || [], { isInit: true });
    }
  }, [field.defaultValue, items, setItems]);

  const toggleCollapsed = () => setCollapsed((prev) => !prev);

  const handleAddItem = () => {
    const newItem = Object.fromEntries(
      field.itemSchema.map((subField) => [
        subField.key,
        subField.defaultValue ?? '',
      ])
    );

    // Adding is the user's own edit, so it is written without `isInit` and the form reads as changed.
    setItems([...(Array.isArray(items) ? items : []), newItem]);
  };

  const handleRemoveItem = (indexToRemove) => {
    setItems((items || []).filter((_, index) => index !== indexToRemove));
  };

  return (
    <Paper variant="outlined" sx={{ p: '16px' }}>
      <CenteredFlexBox justifyContent="space-between">
        <Typography variant="h6">{translate(field.label)}</Typography>
        <CollapseToggle collapsed={collapsed} onToggle={toggleCollapsed} />
      </CenteredFlexBox>
      <Collapse in={!collapsed}>
        {(Array.isArray(items) ? items : []).map((item, index) => (
          <Paper
            key={index}
            variant="outlined"
            sx={{
              p: 2,
              mb: 1,
              display: 'flex',
              gap: 2,
              alignItems: 'center',
            }}
          >
            <SchemaRenderer
              fields={field.itemSchema}
              basePath={`${path}.${index}`}
            />
            <IconButton onClick={() => handleRemoveItem(index)} size="small">
              <DeleteOutlinedIcon />
            </IconButton>
          </Paper>
        ))}
        <Button
          startIcon={<AddIcon />}
          onClick={handleAddItem}
          size="small"
          variant="text"
        >
          {translate('ADD_ITEM')}
        </Button>
      </Collapse>
    </Paper>
  );
};
